<template>
  <div class="w-full px-6 py-4">
    <div class="flex w-full items-center justify-between pb-4 border-b">
      <div>
        <label class="block text-sm text-gray-700 font-medium dark:text-gray-200">
          <span>{{ $t('general.services') }}</span>
        </label>
        <p class="block text-gray-600 text-sm dark:text-gray-300">
          {{ $t('components.voucher.edit.services.enableDisable') }}
        </p>
      </div>
      <div class="relative">
        <text-field v-model="query" :placeholder="$t('service.filter')" />
        <div class="absolute right-0 transform mr-2 top-1/2 -translate-y-1/2">
          <o-button
            v-if="query.length"
            size="xs"
            variant="danger"
            transparent
            flat
            icon="x"
            @click="query = ''"
          />
        </div>
      </div>
    </div>
    <div class="border-b p-2 mb-8">
      <div
        class="w-full flex items-center justify-between mb-2 pb-2 px-1 border-b last:border-none last:mb-0 last:pb-0"
      >
        <p class="text-sm" :class="_all ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'">
          {{ $t('service.enableDisable') }}
        </p>
        <toggle-field v-model="_all" />
      </div>
    </div>
    <div v-for="(category, index) in filtered" :key="`category-${index}`">
      <div v-if="category" class="px-1 mb-3">
        <h5 class="text-gray-700 text-sm font-medium uppercase tracking-wide dark:text-gray-200">
          {{ category.name || $t('general.other') }}
        </h5>
        <p v-if="category.information" class="text-sm text-gray-500 dark:text-gray-400">
          {{ category.information }}
        </p>
      </div>
      <div class="bg-gray-50 rounded-md p-3 mb-8 dark:bg-gray-900">
        <div
          v-for="service in category.services"
          :key="service.idService"
          class="w-full flex items-center justify-between mb-2 pb-2 px-1 border-b last:border-none last:mb-0 last:pb-0"
        >
          <p class="text-sm" :class="_voucher.serviceIDs.includes(service.idService) ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'">
            {{ service.title }}
          </p>
          <toggle-field v-model="_voucher.serviceIDs" :value="service.idService" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import Fuse from 'fuse.js'

import {
  ToggleField,
  TextField,
} from '@/components/fields'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'

interface Props {
  voucher: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:voucher', value: Record<string, any>): void
}>()

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()

const query = ref('')
const all = ref(true)

const _voucher = computed({
  get () {
    return props.voucher
  },
  set (value) {
    emit('update:voucher', value)
  },
})

const services = computed(() => serviceStore.categorised)

const filtered = computed(() => {
  const categories = services.value

  if (query.value.length) {
    const arrays = Object.values(categories).map(({ services }: any) => services)
    const items = ([] as any[]).concat(...arrays)
    const fuse = new Fuse(items, {
      keys: [
        'title',
        'information',
        'price',
      ],
    })
    const results = fuse.search(query.value)
    const svcs = [
      { services: results.map(({ item }) => item) },
    ]
    return svcs
  }

  return categories
})

const ids = computed(() => {
  const arrays = Object.values(filtered.value).map(({ services }: any) => services)
  const svcs = ([] as any[]).concat(...arrays)

  return svcs.map(({ idService }: any) => idService)
})

const _all = computed({
  get () {
    return ids.value.every((id: any) => _voucher.value.serviceIDs.includes(id))
  },
  set (value: boolean) {
    if (value) {
      _voucher.value.serviceIDs = ids.value
    } else {
      const arrays = Object.values(services.value).map(({ services }: any) => services)
      const svcs = ([] as any[]).concat(...arrays)
      const filteredIds = svcs
        .map(({ idService }: any) => idService)
        .filter((id: any) => !ids.value.includes(id))

      _voucher.value.serviceIDs = filteredIds
    }
  },
})

onMounted(() => {
  serviceStore.sync()
  employeeStore.sync()
})
</script>
